export type QRFormContractField = {
  id: string;
  content_key: string;
  content_aliases?: string[];
  form_key: string;
  input_keys?: string[];
  type: string;
  label: string;
  required: boolean;
  default?: string;
  min?: number;
  max?: number;
  options?: string[];
};

export type QRFormContractManifest = {
  id: string;
  type: string;
  builder_key: string;
  name: string;
  summary: string;
  fields: QRFormContractField[];
};

type QRFormContractMap = Record<string, QRFormContractManifest>;
type FieldControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

const contractCache = new WeakMap<Document, QRFormContractMap>();

function contractDocument(root: ParentNode | Document = document): Document {
  if (root instanceof Document) {
    return root;
  }
  return root.ownerDocument || document;
}

function normalizeTypeKey(value: string): string {
  return String(value || "").trim().toUpperCase();
}

function parseContractsDocument(doc: Document): QRFormContractMap {
  const cached = contractCache.get(doc);
  if (cached) {
    return cached;
  }

  const script = doc.getElementById("qr-form-contracts-data");
  const parsed: QRFormContractMap = {};
  if (script) {
    try {
      const raw = JSON.parse(String(script.textContent || "{}")) as QRFormContractMap;
      Object.entries(raw || {}).forEach(([key, manifest]) => {
        const normalized = normalizeTypeKey(key);
        if (normalized && manifest && typeof manifest === "object") {
          parsed[normalized] = manifest;
        }
      });
    } catch {
      // Ignore malformed inline payloads and fall back to an empty catalog.
    }
  }

  contractCache.set(doc, parsed);
  return parsed;
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach((value) => {
    const normalized = String(value || "").trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    out.push(normalized);
  });
  return out;
}

function cssEscape(value: string): string {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(String(value || ""));
  }
  return String(value || "").replace(/(["\\\[\]#.:])/g, "\\$1");
}

function fieldMatches(field: QRFormContractField, key: string): boolean {
  const normalized = String(key || "").trim();
  if (!normalized) {
    return false;
  }
  if (field.id === normalized || field.form_key === normalized || field.content_key === normalized) {
    return true;
  }
  if (Array.isArray(field.content_aliases) && field.content_aliases.includes(normalized)) {
    return true;
  }
  return Array.isArray(field.input_keys) && field.input_keys.includes(normalized);
}

function isFieldControl(node: Element | null): node is FieldControl {
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement;
}

export function getQRFormContractMap(root: ParentNode | Document = document): QRFormContractMap {
  return parseContractsDocument(contractDocument(root));
}

export function activeQRFormType(form: HTMLFormElement): string {
  const checked = form.querySelector<HTMLInputElement>('input[name="type"]:checked');
  if (checked) {
    return normalizeTypeKey(checked.value);
  }
  const hidden = form.querySelector<HTMLInputElement>('[data-qr-type-hidden], input[name="type"]');
  if (hidden) {
    return normalizeTypeKey(hidden.value);
  }
  const dataType = form.getAttribute("data-qr-type");
  if (dataType) {
    return normalizeTypeKey(dataType);
  }
  return normalizeTypeKey(document.body?.dataset?.preselectedType || "");
}

export function getQRFormContract(typeKey: string, root: ParentNode | Document = document): QRFormContractManifest | null {
  const contracts = getQRFormContractMap(root);
  return contracts[normalizeTypeKey(typeKey)] || null;
}

export function getQRFormField(
  manifest: QRFormContractManifest | null,
  fieldKey: string,
): QRFormContractField | null {
  if (!manifest) {
    return null;
  }
  const normalized = String(fieldKey || "").trim();
  if (!normalized) {
    return null;
  }
  return manifest.fields.find((field) => fieldMatches(field, normalized)) || null;
}

export function getQRFormFieldInputNames(field: QRFormContractField | null): string[] {
  if (!field) {
    return [];
  }
  return uniqueStrings([
    ...(Array.isArray(field.input_keys) ? field.input_keys : []),
    field.form_key,
    field.id,
    field.content_key,
    ...(Array.isArray(field.content_aliases) ? field.content_aliases : []),
  ]);
}

export function getQRFormFieldControls(
  form: HTMLFormElement,
  fieldOrKey: QRFormContractField | string,
  typeKey = activeQRFormType(form),
): FieldControl[] {
  const manifest = getQRFormContract(typeKey, form);
  const field = typeof fieldOrKey === "string" ? getQRFormField(manifest, fieldOrKey) : fieldOrKey;
  const names = getQRFormFieldInputNames(field);
  const controls: FieldControl[] = [];
  const seen = new Set<Element>();

  names.forEach((name) => {
    form.querySelectorAll(`[name="${cssEscape(name)}"]`).forEach((node) => {
      if (!isFieldControl(node) || seen.has(node)) {
        return;
      }
      seen.add(node);
      controls.push(node);
    });
  });

  return controls;
}

export function getQRFormPrimaryFieldControl(
  form: HTMLFormElement,
  fieldOrKey: QRFormContractField | string,
  typeKey = activeQRFormType(form),
): FieldControl | null {
  const controls = getQRFormFieldControls(form, fieldOrKey, typeKey);
  return controls.find((control) => {
    const element = control as HTMLElement;
    return !element.closest("[hidden]") && !element.closest(".hidden");
  }) || controls[0] || null;
}
